import React, { useState, useEffect, useCallback, useRef } from 'react';
import { uploadManifests, getManifests, getUnmatchedPages, assignPacketPage, getPendingRateCons, assignRateCon } from '../truckmateStore';
import { playOpen } from '../clickSound';
import DropZone from './dispatch/DropZone';
import './dispatch/dispatch.css';

// ---- Outbound trip sheets ---------------------------------------------------
// Once a day the dispatcher photographs/scans the paper manifests and uploads
// them here in one batch. The AI reads printed AND handwritten text, splits the
// pages into trips, and the Watchtower then uses the sheets for the real stop
// order, handwritten appointments, the pickup plan and call-aheads.

const PANEL = '#FFFFFF', PANEL2 = '#F1F5F9', LINE = '#E2E8F0';
const INK = '#172033', INK2 = '#64748B', INK3 = '#94A3B8';
const NEON = '#1677FF', GREEN = '#10B981', AMBER = '#F59E0B', RED = '#EF4444', VIOLET = '#8B5CF6';
const card = { background: PANEL, border: `1px solid ${LINE}`, borderRadius: '14px', boxShadow: '0 1px 2px rgba(15,23,42,.05), 0 12px 28px -20px rgba(15,23,42,.28)' };
const secLbl = { fontSize: '10px', fontWeight: 800, color: INK2, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '7px' };
const chip = (bg, fg) => ({ display: 'inline-block', background: bg, color: fg, borderRadius: '6px', padding: '2px 7px', fontSize: '11px', fontWeight: 700, marginRight: '6px', marginTop: '4px' });

const apptText = (s) => (s.apptDate || s.apptTime ? `${s.apptDate || ''} ${s.apptTime || ''}`.trim() : '');
const cityState = (s) => [s.city, s.state].filter(Boolean).join(', ');

// One trip's sheet: stops in the order the driver runs them, with appointments,
// call-aheads, handwritten notes and every difference from TruckMate.
export function TripSheetCard({ sheet, deliveredCities }) {
  if (!sheet) return null;
  const delivered = deliveredCities || new Set();
  const diffs = sheet.diffs || [];
  const stops = (sheet.stops || []).filter((s) => !/^LOAD$/i.test(s.action || ''));
  return (
    <div style={{ ...card, padding: '12px 14px', marginBottom: '12px', borderLeft: `3px solid ${VIOLET}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '6px' }}>
        <div style={{ ...secLbl, color: VIOLET, marginBottom: 0 }}>📄 Trip sheet — stop order & notes</div>
        <div style={{ fontSize: '11px', color: INK3 }}>Uploaded {new Date(sheet.uploadedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}{sheet.uploadedBy ? ` by ${sheet.uploadedBy}` : ''}</div>
      </div>

      {(sheet.pickupPlan || (sheet.handwrittenNotes || []).length > 0) && (
        <div style={{ background: 'rgba(139,92,246,.08)', borderRadius: '10px', padding: '8px 10px', margin: '8px 0', fontSize: '12.5px', color: INK }}>
          {sheet.pickupPlan && <div>✍️ <b>Pickup:</b> {sheet.pickupPlan}</div>}
          {(sheet.handwrittenNotes || []).map((n, i) => <div key={i}>✍️ {n}</div>)}
        </div>
      )}

      {diffs.length > 0 && (
        <div style={{ background: 'rgba(239,68,68,.07)', border: `1px solid ${RED}44`, borderRadius: '10px', padding: '8px 10px', margin: '8px 0' }}>
          <div style={{ fontSize: '11px', fontWeight: 800, color: '#B91C1C', marginBottom: '3px' }}>⚠️ Trip sheet ≠ TruckMate</div>
          {diffs.map((d, i) => <div key={i} style={{ fontSize: '12px', color: '#B91C1C' }}>• {d.msg}</div>)}
        </div>
      )}

      {stops.map((s, i) => {
        const done = delivered.has(`${String(s.city || '').toUpperCase()}|${String(s.state || '').toUpperCase()}`);
        return (
          <div key={i} style={{ display: 'flex', gap: '10px', padding: '7px 0', borderTop: `1px solid ${LINE}`, opacity: done ? 0.55 : 1 }}>
            <div style={{ minWidth: '26px', textAlign: 'center', fontWeight: 800, color: done ? GREEN : (s.subStop ? INK3 : NEON), fontSize: '13px' }}>{done ? '✓' : (s.subStop ? '+' : s.stopNumber)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: INK }}>{s.customer || '—'} <span style={{ fontWeight: 500, color: INK2 }}>· {cityState(s)}</span></div>
              <div style={{ fontSize: '11.5px', color: INK2 }}>{s.piecesText || (s.pieces != null ? `${s.pieces} boxes` : '')}</div>
              <div>
                {apptText(s) && <span style={chip(s.apptSource === 'handwritten' ? 'rgba(139,92,246,.14)' : 'rgba(22,119,255,.12)', s.apptSource === 'handwritten' ? VIOLET : NEON)}>⏰ Appt {apptText(s)}{s.apptSource === 'handwritten' ? ' ✍️' : ''}</span>}
                {s.receivingHours && <span style={chip(PANEL2, INK2)}>🕘 {s.receivingHours}</span>}
                {(s.callAhead || []).map((c, j) => <span key={j} style={chip('rgba(245,158,11,.16)', '#B45309')}>📞 {c.method === 'text' ? 'Text' : 'Call'} {[c.contact, c.phone].filter(Boolean).join(' ')} · {c.leadMinutes != null ? (c.leadMinutes >= 60 ? `${c.leadMinutes / 60}h` : `${c.leadMinutes}m`) : ''} before</span>)}
                {(s.handwritten || []).map((h, j) => <span key={`h${j}`} style={chip('rgba(139,92,246,.10)', VIOLET)}>✍️ {h}</span>)}
              </div>
              {(s.instructions || []).map((n, j) => <div key={j} style={{ fontSize: '11.5px', color: INK, marginTop: '3px' }}>• {n}</div>)}
            </div>
          </div>
        );
      })}

      {(sheet.generalInstructions || []).length > 0 && (
        <div style={{ marginTop: '8px', fontSize: '11.5px', color: INK2 }}>
          <div style={{ ...secLbl, marginBottom: '3px' }}>Trip rules</div>
          {sheet.generalInstructions.map((n, i) => <div key={i}>• {n}</div>)}
        </div>
      )}
      {(sheet.unreadable || []).length > 0 && (
        <div style={{ marginTop: '8px', fontSize: '11.5px', color: AMBER, fontWeight: 600 }}>❓ Couldn’t read: {sheet.unreadable.join(' · ')}</div>
      )}
    </div>
  );
}

function SheetSummary({ t, onOpenTrip }) {
  const stops = (t.stops || []).filter((s) => /DELIVER|PICKUP/i.test(s.action || ''));
  const appts = stops.filter((s) => s.apptDate || s.apptTime);
  const calls = stops.reduce((n, s) => n + (s.callAhead || []).length, 0);
  const diffs = t.diffs || [];
  // attached by trip number as soon as the trip is on the board; differences are
  // notes for review and never hold the sheet back
  const waiting = t.onBoard === false;
  return (
    <div style={{ ...card, padding: '12px 14px', marginBottom: '10px', borderLeft: `4px solid ${waiting ? LINE : GREEN}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: 800, color: INK }}>Trip {t.tripNumber} <span style={{ fontWeight: 600, color: INK2, fontSize: '12.5px' }}>· Truck {t.truck || '—'} · Trailer {t.trailer || '—'}{t.sheetSequence ? ` · ${t.sheetSequence}` : ''}</span></div>
          <div style={{ fontSize: '12px', color: INK2, marginTop: '2px' }}>{(t.drivers || []).map((d) => d.name).filter(Boolean).join(' & ') || '—'}</div>
          <div style={{ marginTop: '4px' }}>
            {waiting
              ? <span style={chip(PANEL2, INK2)}>⏳ Trip not in TruckMate yet — attaches automatically when it appears</span>
              : <span style={chip('rgba(16,185,129,.12)', '#047857')}>✓ Added to load · stop order from sheet</span>}
            <span style={chip(PANEL2, INK)}>{stops.length} stops</span>
            {appts.length > 0 && <span style={chip('rgba(22,119,255,.12)', NEON)}>⏰ {appts.length} appointment{appts.length === 1 ? '' : 's'}</span>}
            {calls > 0 && <span style={chip('rgba(245,158,11,.16)', '#B45309')}>📞 {calls} call-ahead{calls === 1 ? '' : 's'}</span>}
            {t.pickupPlan && <span style={chip('rgba(139,92,246,.12)', VIOLET)}>✍️ {t.pickupPlan}</span>}
            {!waiting && (diffs.length > 0 ? <span style={chip('rgba(245,158,11,.16)', '#B45309')}>{diffs.length} note{diffs.length === 1 ? '' : 's'} vs TruckMate</span> : <span style={chip('rgba(16,185,129,.12)', '#047857')}>✓ matches TruckMate</span>)}
            {(t.unreadable || []).length > 0 && <span style={chip('rgba(245,158,11,.16)', '#B45309')}>❓ {t.unreadable.length} unclear</span>}
          </div>
        </div>
        {onOpenTrip && <button onClick={() => { playOpen(); onOpenTrip(t.tripNumber); }} style={{ alignSelf: 'flex-start', padding: '6px 11px', borderRadius: '8px', border: `1px solid ${LINE}`, background: PANEL, color: NEON, fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>Open load →</button>}
      </div>
      {!waiting && diffs.length > 0 && (
        <div style={{ marginTop: '6px' }}>
          <div style={{ fontSize: '11.5px', color: INK2, fontWeight: 700 }}>For review — the sheet is already in use; fix TruckMate or the sheet if these are real:</div>
          {diffs.map((d, i) => <div key={i} style={{ fontSize: '12px', color: '#92400E' }}>• {d.msg}</div>)}
        </div>
      )}
    </div>
  );
}

const TYPE_LABEL = { manifest: 'Manifest', manifest_continuation: 'Manifest (cont.)', driver_instructions: 'Driver instructions', loading_sheet: 'Loading sheet', rate_confirmation: 'Rate confirmation', carrier_confirmation: 'Carrier confirmation', email: 'Email', bill_of_lading: 'Bill of lading', packing_slip: 'Packing slip', shipping_ticket: 'Shipping ticket', proof_of_delivery: 'Proof of delivery', driver_id: 'Driver ID (restricted)', invoice: 'Invoice', shipment_notice: 'Shipment notice', other: 'Other' };

// Pages of a packet nobody could match to a trip — a dispatcher assigns them.
function UnmatchedPages({ refreshKey }) {
  const [list, setList] = useState(null);
  const [trip, setTrip] = useState({});
  const [err, setErr] = useState('');
  const load = useCallback(() => getUnmatchedPages().then(setList).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!list || !list.length) return null;
  const key = (p) => `${p.batchId}-${p.file}-${p.page}`;
  return (
    <div style={{ ...card, padding: '14px', marginBottom: '12px', borderLeft: `3px solid ${AMBER}` }}>
      <div style={{ fontSize: '14px', fontWeight: 700 }}>Pages not matched to a trip ({list.length})</div>
      <div style={{ fontSize: '12.5px', margin: '4px 0 8px' }}>Trip-sheet pages the AI couldn't tie to a trip (usually a continuation page without its first page). Enter the trip they belong to.</div>
      {err && <div style={{ fontSize: '12.5px', color: '#B91C1C' }}>{err}</div>}
      {list.map((p) => (
        <form key={key(p)} onSubmit={async (e) => { e.preventDefault(); try { await assignPacketPage({ batchId: p.batchId, file: p.file, page: p.page, trip: trip[key(p)] }); load(); } catch (ex) { setErr(ex.message); } }}
          style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', padding: '6px 0', borderTop: `1px solid ${LINE}` }}>
          <span style={{ fontSize: '12.5px', flex: '1 1 260px' }}><b>Page {p.page}</b> · {TYPE_LABEL[p.type] || p.type} — {p.type === 'driver_id' ? `${p.driverName || 'Driver'} ID` : p.summary} <span style={{ fontSize: '11.5px' }}>({p.matchedBy})</span></span>
          <input value={trip[key(p)] || ''} onChange={(e) => setTrip((t) => ({ ...t, [key(p)]: e.target.value }))} placeholder="Trip #" inputMode="numeric" style={{ width: '110px', border: `1px solid ${LINE}`, borderRadius: '6px', padding: '5px 8px' }} />
          <button type="submit" disabled={!trip[key(p)]} style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid #9baec8', background: '#dfe7f2', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Assign</button>
        </form>
      ))}
    </div>
  );
}

// Rate cons from a packet that no bill / reference / truck tied to a load.
function PendingRateCons({ refreshKey }) {
  const [list, setList] = useState(null);
  const [trip, setTrip] = useState({});
  const [err, setErr] = useState('');
  const load = useCallback(() => getPendingRateCons().then(setList).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!list || !list.length) return null;
  return (
    <div style={{ ...card, padding: '14px', marginBottom: '12px', borderLeft: `3px solid ${AMBER}` }}>
      <div style={{ fontSize: '14px', fontWeight: 700 }}>Rate cons not matched to a load ({list.length})</div>
      <div style={{ fontSize: '12.5px', margin: '4px 0 8px' }}>No RC- bill label, broker load number or truck tied these to a load. Enter the trip they belong to.</div>
      {err && <div style={{ fontSize: '12.5px', color: '#B91C1C' }}>{err}</div>}
      {list.map((r) => (
        <form key={r.id} onSubmit={async (e) => { e.preventDefault(); try { await assignRateCon(r.id, trip[r.id]); load(); } catch (ex) { setErr(ex.message); } }}
          style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', padding: '6px 0', borderTop: `1px solid ${LINE}` }}>
          <span style={{ fontSize: '12.5px', flex: '1 1 260px' }}><b>{r.broker || 'Rate con'}</b>{r.loadNumber ? ` · load ${r.loadNumber}` : ''}{r.truck ? ` · truck ${r.truck}` : ''} — {r.summary} <span style={{ fontSize: '11.5px' }}>({r.filename})</span></span>
          <input value={trip[r.id] || ''} onChange={(e) => setTrip((t) => ({ ...t, [r.id]: e.target.value }))} placeholder="Trip #" inputMode="numeric" style={{ width: '110px', border: `1px solid ${LINE}`, borderRadius: '6px', padding: '5px 8px' }} />
          <button type="submit" disabled={!trip[r.id]} style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid #9baec8', background: '#dfe7f2', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Attach</button>
        </form>
      ))}
    </div>
  );
}

export default function TripSheetsPanel({ onOpenTrip }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState('');
  const [err, setErr] = useState('');
  const [sheets, setSheets] = useState(null);
  const [justRead, setJustRead] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const inputRef = useRef(null);

  const load = useCallback(() => getManifests().then(setSheets).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!busy) return undefined;
    setElapsed(0);
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [busy]);

  const read = async () => {
    setErr(''); setBusy(true); setJustRead(null);
    try {
      const r = await uploadManifests(files, undefined, setStage);
      setJustRead({ trips: r.trips || [], pages: r.pages || [], skipped: r.skipped || [], rateCons: r.rateCons || [] });
      setFiles([]); if (inputRef.current) inputRef.current.value = '';
      load();
    } catch (e) { setErr(e.message || 'Upload failed.'); }
    finally { setBusy(false); }
  };

  const today = new Date().toDateString();
  const todays = (sheets || []).filter((s) => new Date(s.uploadedAt).toDateString() === today);
  const older = (sheets || []).filter((s) => new Date(s.uploadedAt).toDateString() !== today);

  return (
    <div>
      <DropZone onFiles={(fs) => setFiles((cur) => [...cur, ...fs])} disabled={busy} label="Drop the trip sheets or the whole packet here" style={{ ...card, padding: '14px', marginBottom: '12px', position: 'relative' }}>
        <div style={{ fontSize: '14px', fontWeight: 800, color: INK }}>📄 Upload trip sheets and rate cons</div>
        <div style={{ fontSize: '12.5px', color: INK2, margin: '4px 0 10px' }}>
          Photograph or scan every page (both pages of each trip), then select them all at once. The AI reads the printed text and the handwriting —
          appointments, who picks up and when, extra pallets, call-ahead numbers — and checks each trip against TruckMate.
          {' '}<b>Drag the files onto this box</b> or choose them. You can drop the whole scanned packet: the trip sheets (manifest + continuation page) and every rate con are kept and attached to their loads — rate cons by the RC- bill label, broker load # or truck. BOLs, PODs, invoices, loading sheets and the instruction page are skipped.
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input ref={inputRef} type="file" multiple accept="image/*,application/pdf" disabled={busy}
            onChange={(e) => setFiles([...(e.target.files || [])])} style={{ fontSize: '12.5px' }} />
          {files.length > 0 && !busy && <span style={{ fontSize: '12px', color: INK2 }}>{files.length} file{files.length === 1 ? '' : 's'} ready: {files.slice(0, 3).map((f) => f.name).join(', ')}{files.length > 3 ? '…' : ''} · <button type="button" onClick={() => { setFiles([]); if (inputRef.current) inputRef.current.value = ''; }} style={{ border: 'none', background: 'none', color: NEON, cursor: 'pointer', fontSize: '12px', padding: 0 }}>clear</button></span>}
          <button onClick={read} disabled={busy || !files.length}
            style={{ padding: '8px 14px', borderRadius: '9px', border: 'none', background: files.length && !busy ? NEON : INK3, color: '#fff', fontSize: '13px', fontWeight: 700, cursor: files.length && !busy ? 'pointer' : 'default' }}>
            {busy ? `${stage === 'storing' ? 'Saving originals' : 'Reading'}… ${elapsed}s` : files.length ? `Read ${files.length} file${files.length === 1 ? '' : 's'}` : 'Read pages'}
          </button>
        </div>
        {busy && <div style={{ fontSize: '12px', color: INK2, marginTop: '8px' }}>First the AI sorts every page (only the trip sheets go on), then reads the trip sheets a few at a time — a full day’s packet takes about 2–5 minutes. Keep this tab open.</div>}
        {err && <div style={{ fontSize: '12.5px', color: '#B91C1C', marginTop: '8px', fontWeight: 600 }}>{err}</div>}
      </DropZone>

      {justRead && (
        <div style={{ ...card, padding: '12px 14px', marginBottom: '12px', fontSize: '12.5px' }}>
          {justRead.rateCons.length > 0 && (
            <div style={{ fontWeight: 700, color: '#047857', marginBottom: '4px' }}>✓ Read {justRead.rateCons.length} rate con{justRead.rateCons.length === 1 ? '' : 's'}: {justRead.rateCons.map((r) => `${r.broker || 'rate con'}${r.trip ? ` → ${r.trip}` : ' → needs a trip'}`).join(' · ')}</div>
          )}
          {justRead.trips.length > 0 && <div style={{ fontWeight: 700, color: '#047857' }}>✓ Read {justRead.trips.length} manifest{justRead.trips.length === 1 ? '' : 's'}: {justRead.trips.map((t) => `${t.tripNumber}${t.outsideCarrier && t.outsideCarrier.isOutsideCarrier ? ` (OC · ${t.outsideCarrier.name || 'carrier'})` : ''}`).join(', ') || '—'}</div>}
          {justRead.pages.length > 0 && (
            <div style={{ marginTop: '6px' }}>
              Kept {justRead.pages.length} trip-sheet page{justRead.pages.length === 1 ? '' : 's'}{justRead.pages.some((p) => !p.trip) ? ` · ${justRead.pages.filter((p) => !p.trip).length} need a trip (below)` : ' · all attached to their trips'}
            </div>
          )}
          {justRead.skipped.length > 0 && (
            <div style={{ marginTop: '4px', color: INK2 }}>
              Skipped {justRead.skipped.length} other page{justRead.skipped.length === 1 ? '' : 's'}: {Object.entries(justRead.skipped.reduce((m, p) => { m[p.type] = (m[p.type] || 0) + 1; return m; }, {})).map(([t, n]) => `${n} ${TYPE_LABEL[t] || t}`).join(' · ')}
            </div>
          )}
        </div>
      )}
      <UnmatchedPages refreshKey={justRead} />
      <PendingRateCons refreshKey={justRead} />

      <div style={{ ...secLbl, margin: '4px 2px 8px' }}>Today ({todays.length})</div>
      {sheets && todays.length === 0 && <div style={{ ...card, padding: '22px', textAlign: 'center', color: INK2, fontSize: '13px' }}>No trip sheets uploaded today yet.</div>}
      {todays.map((t) => <SheetSummary key={t.tripNumber} t={t} onOpenTrip={onOpenTrip} />)}

      {older.length > 0 && (
        <>
          <div style={{ ...secLbl, margin: '14px 2px 8px' }}>Earlier ({older.length})</div>
          {older.map((t) => <SheetSummary key={t.tripNumber} t={t} onOpenTrip={onOpenTrip} />)}
        </>
      )}
    </div>
  );
}
